/* Owns `/s/:serverId` (PANEL.md §2): resolves `ServerDto` + `StatsHealth`,
   publishes the active server to `lib/auth-store.ts` so every permission check
   and the switcher agree on it, points the socket subscription at that server,
   and renders the child route. Child-route permissions are gated one level down
   by `RequireRole`, so this layout only has to answer "does this server exist
   and may I read it at all". */

import { useQuery } from '@tanstack/react-query'
import { useEffect, type ReactNode } from 'react'
import { Outlet, useParams } from 'react-router'

import { ErrorState } from '../components/ui/ErrorState.js'
import { Skeleton } from '../components/ui/Skeleton.js'
import { ApiHttpError } from '../lib/api.js'
import { setActiveServerId } from '../lib/auth-store.js'
import { wsClient } from '../lib/ws.js'
import { healthQueryOptions, serverQueryOptions } from './api.js'

export function ServerLayout(): ReactNode {
  const params = useParams()
  const serverId = params['serverId'] ?? ''

  const server = useQuery({ ...serverQueryOptions(serverId), enabled: serverId !== '' })
  // Health is fetched here rather than in the strip so a child route that needs
  // it (dashboard, config) shares one in-flight request.
  useQuery({ ...healthQueryOptions(serverId), enabled: serverId !== '' })

  useEffect(() => {
    if (!serverId) return
    setActiveServerId(serverId)
    // The client debounces subscription changes by 250 ms, so navigating
    // between two servers produces one subscribe, not a subscribe storm.
    wsClient.setSubscriptions([serverId])
    return () => {
      setActiveServerId(null)
      wsClient.setSubscriptions([])
    }
  }, [serverId])

  if (server.isPending) {
    return (
      <div className="flex flex-col gap-4 p-4 lg:p-6" aria-busy="true">
        <Skeleton height="2rem" rounded="md" />
        <Skeleton height="12rem" rounded="lg" />
      </div>
    )
  }

  if (server.isError) {
    const error = server.error
    return (
      <div className="p-4 lg:p-6">
        <ErrorState
          {...(error instanceof ApiHttpError ? { code: error.code } : {})}
          {...(error instanceof ApiHttpError && error.reqId ? { reqId: error.reqId } : {})}
          onRetry={() => void server.refetch()}
        />
      </div>
    )
  }

  return <Outlet />
}
